import { useAuth } from '@/features/authentication';
import { dashboardApi, matchesApi } from '@/features/profile-management';
import { getStorageUrl } from '@/shared/lib';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, CheckCircle2, Eye, Heart, MessageCircle, Sparkles, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function DashboardPage() {
  const { user } = useAuth();

  const { data: dashboardData, isLoading: isDashboardLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: dashboardApi.getDashboard,
  });

  const { data: matches, isLoading: isMatchesLoading } = useQuery({
    queryKey: ['matches'],
    queryFn: matchesApi.getMatches,
  });

  const stats = [
    {
      title: 'Vues du profil',
      value: dashboardData?.profileViews ?? 0,
      icon: Eye,
      color: 'text-primary',
      bgColor: 'bg-primary-surface',
    },
    {
      title: 'Connexions',
      value: dashboardData?.matchCount ?? 0,
      icon: Heart,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
    },
    {
      title: 'Messages',
      value: dashboardData?.messageCount ?? 0,
      icon: MessageCircle,
      color: 'text-success',
      bgColor: 'bg-success/10',
    },
    {
      title: 'Demandes reçues',
      value: dashboardData?.trustRequestsReceivedCount ?? 0,
      icon: Users,
      color: 'text-primary',
      bgColor: 'bg-primary-surface',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">
            Bon retour, {user?.username || 'Utilisateur'} !
          </h1>
          <p className="text-muted-foreground mt-2">
            Voici un aperçu de votre activité sur Zawajme.
          </p>
        </div>
        <Button asChild>
          <Link to="/profiles">
            <Sparkles className="h-4 w-4 mr-2" />
            Découvrir des profils
          </Link>
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.title} className="relative overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
              <div
                className={`h-10 w-10 rounded-lg ${stat.bgColor} flex items-center justify-center`}
              >
                <stat.icon className={`h-5 w-5 ${stat.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              {isDashboardLoading ? (
                <Skeleton className="h-9 w-20" />
              ) : (
                <div className="text-3xl font-display font-semibold">{stat.value}</div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Connections */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-display text-xl">Connexions récentes</CardTitle>
            <CardDescription>Les personnes avec qui vous avez établi un contact</CardDescription>
          </div>
          <Button variant="outline" asChild>
            <Link to="/profiles">
              Voir tout
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {isMatchesLoading ? (
            <div className="flex gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex flex-col items-center gap-3">
                  <Skeleton className="h-20 w-20 rounded-full" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-3 w-16" />
                </div>
              ))}
            </div>
          ) : matches && matches.length > 0 ? (
            <div className="flex flex-wrap gap-6">
              {matches.slice(0, 8).map((match) => (
                <Link
                  key={match.nodeId}
                  to={`/profile/${match.nodeId}`}
                  className="group flex flex-col items-center gap-3 p-3 rounded-xl hover:bg-primary-surface transition-all duration-300"
                >
                  <div className="relative">
                    <Avatar className="h-20 w-20 ring-2 ring-border group-hover:ring-primary/30 transition-all duration-300">
                      <AvatarImage src={getStorageUrl(match.avatarPath)} alt={match.username} />
                      <AvatarFallback className="bg-primary text-primary-foreground text-lg font-semibold">
                        {match.username?.charAt(0).toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    {match.isPhotoVerified && (
                      <div className="absolute -bottom-1 -right-1 h-6 w-6 bg-success rounded-full flex items-center justify-center border-2 border-card">
                        <CheckCircle2 className="h-3.5 w-3.5 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <span className="text-sm font-medium block">{match.username}</span>
                    {Number(match.age) > 0 && (
                      <span className="text-xs text-muted-foreground">{match.age} ans</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="h-16 w-16 mx-auto bg-primary-surface rounded-full flex items-center justify-center mb-4">
                <Heart className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Pas encore de connexions</h3>
              <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                Commencez à découvrir des profils et envoyez des demandes de contact pour établir
                vos premières connexions.
              </p>
              <Button asChild>
                <Link to="/profiles">Découvrir des profils</Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="group cursor-pointer border-border hover:border-primary/30 hover:shadow-lg transition-all duration-300">
          <Link to="/profiles">
            <CardHeader>
              <div className="h-12 w-12 rounded-xl bg-primary-surface flex items-center justify-center mb-3 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <Users className="h-6 w-6 text-primary group-hover:text-primary-foreground transition-colors duration-300" />
              </div>
              <CardTitle className="font-display">Découvrir</CardTitle>
              <CardDescription>Parcourez les profils compatibles avec vos critères</CardDescription>
            </CardHeader>
          </Link>
        </Card>

        <Card className="group cursor-pointer border-border hover:border-primary/30 hover:shadow-lg transition-all duration-300">
          <Link to="/chat">
            <CardHeader>
              <div className="h-12 w-12 rounded-xl bg-success/10 flex items-center justify-center mb-3 group-hover:bg-success group-hover:scale-110 transition-all duration-300">
                <MessageCircle className="h-6 w-6 text-success group-hover:text-white transition-colors duration-300" />
              </div>
              <CardTitle className="font-display">Messages</CardTitle>
              <CardDescription>
                Échangez avec vos connexions en toute confidentialité
              </CardDescription>
            </CardHeader>
          </Link>
        </Card>

        <Card className="group cursor-pointer border-border hover:border-primary/30 hover:shadow-lg transition-all duration-300">
          <Link to="/profile">
            <CardHeader>
              <div className="h-12 w-12 rounded-xl bg-accent/10 flex items-center justify-center mb-3 group-hover:bg-accent group-hover:scale-110 transition-all duration-300">
                <Heart className="h-6 w-6 text-accent group-hover:text-accent-foreground transition-colors duration-300" />
              </div>
              <CardTitle className="font-display">Mon profil</CardTitle>
              <CardDescription>Complétez et mettez à jour vos informations</CardDescription>
            </CardHeader>
          </Link>
        </Card>
      </div>

      {/* Profile completion prompt - can be enhanced when API supports profile completion status */}
      {user && !user.bio && (
        <Card className="border-accent/30 bg-gradient-to-r from-accent/5 to-accent/10">
          <CardHeader>
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-accent/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-6 w-6 text-accent" />
              </div>
              <div className="flex-1">
                <CardTitle className="font-display text-lg">Complétez votre profil</CardTitle>
                <CardDescription className="mt-1">
                  Un profil complet augmente vos chances de trouver la bonne personne. Ajoutez vos
                  photos et détails personnels.
                </CardDescription>
                <Button className="mt-4" variant="premium" asChild>
                  <Link to="/profile">
                    Compléter mon profil
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>
      )}
    </div>
  );
}
